import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft, Calendar as CalendarIcon, Clock, FileText, Paperclip, User as UserIcon,
  Check, X, AlertTriangle, ShieldCheck, MessageSquareWarning, CalendarOff,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import { leaveService, fileUrl } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDate, fmtDateTime, fmtTime12 } from '@/utils/format';

const TYPE_LABELS = {
  sick: 'Sick leave',
  casual: 'Casual leave',
  paid: 'Paid leave',
  wfh: 'Work From Home',
  'half-day': 'Half day',
  short: 'Short leave',
};

export default function LeaveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [showCancel, setShowCancel] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['leave', id],
    queryFn: () => leaveService.get(id),
  });

  const decide = useMutation({
    mutationFn: ({ status, adminNote }) => leaveService.decide(id, { status, adminNote }),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['leave', id] });
      qc.invalidateQueries({ queryKey: ['leaves'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (isLoading) return <Loader />;
  if (error?.response?.status === 403) {
    return <div className="text-center py-16 text-slate-500">You don't have access to this leave.</div>;
  }
  if (!data?.leave) {
    return (
      <div className="text-center py-16">
        <div className="font-semibold text-lg">Leave not found</div>
        <Link to="/leaves" className="text-brand-600 hover:underline text-sm">Back to leaves</Link>
      </div>
    );
  }

  const l = data.leave;
  const isOwner = String(l.user?._id) === String(user?._id);
  const cancellable = isOwner && (l.status === 'pending' || l.status === 'approved');
  const reviewable = isAdmin && l.status === 'pending';
  const backTo = isAdmin && !isOwner ? '/admin/leaves' : '/leaves';

  return (
    <div className="space-y-6">
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600">
        <ArrowLeft size={14} /> Back to leaves
      </Link>

      <PageHeader
        title={TYPE_LABELS[l.type] || l.type}
        subtitle={`Applied on ${fmtDateTime(l.createdAt)}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={l.status} />
            {reviewable && (
              <>
                <button
                  onClick={() => decide.mutate({ status: 'approved' })}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 px-3 py-1.5 rounded-lg"
                  disabled={decide.isPending}
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  onClick={() => setShowReject(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1.5 rounded-lg"
                >
                  <X size={14} /> Reject
                </button>
              </>
            )}
            {cancellable && (
              <button
                onClick={() => setShowCancel(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1.5 rounded-lg"
              >
                <CalendarOff size={14} /> Cancel leave
              </button>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              {l.type === 'short' ? <Clock size={16} /> : <CalendarIcon size={16} />}
              {l.type === 'short' ? 'Time window' : 'Date range'}
            </div>
            {l.type === 'short' ? (
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Date" value={fmtDate(l.startDate)} />
                <Field label="From" value={fmtTime12(l.startTime)} />
                <Field label="To" value={fmtTime12(l.endTime)} />
                <div className="sm:col-span-3">
                  <Field label="Duration" value={`${(l.hours || 0).toFixed(2)} hours`} highlight />
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Start" value={fmtDate(l.startDate)} />
                <Field label="End" value={fmtDate(l.endDate)} />
                <Field label="Duration" value={`${l.days} day${l.days === 1 ? '' : 's'}`} highlight />
              </div>
            )}
          </Card>

          <Card>
            <div className="font-semibold mb-2 flex items-center gap-2"><FileText size={16} /> Reason</div>
            <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
              {l.reason || <span className="italic text-slate-400">No reason provided.</span>}
            </p>
            {l.attachment && (
              <a
                href={fileUrl(l.attachment)}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:underline"
              >
                <Paperclip size={14} /> View attachment
              </a>
            )}
          </Card>

          {(l.status === 'rejected' || l.status === 'approved') && l.reviewedBy && (
            <Card>
              <div className="font-semibold mb-3 flex items-center gap-2">
                <ShieldCheck size={16} /> Admin decision
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Reviewed by" value={l.reviewedBy?.name || '—'} sub={l.reviewedBy?.email} />
                <Field label="Reviewed at" value={fmtDateTime(l.reviewedAt)} />
              </div>
              {l.adminNote && (
                <div className="mt-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Admin note</div>
                  <div className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                    l.status === 'rejected'
                      ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30'
                      : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30'
                  }`}>
                    {l.adminNote}
                  </div>
                </div>
              )}
            </Card>
          )}

          {l.status === 'cancelled' && (
            <Card>
              <div className="font-semibold mb-3 flex items-center gap-2 text-rose-600">
                <MessageSquareWarning size={16} /> Cancellation
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {l.cancelledAt && <Field label="Cancelled at" value={fmtDateTime(l.cancelledAt)} />}
              </div>
              {l.cancellationReason && (
                <div className="mt-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Reason</div>
                  <div className="px-3 py-2 rounded-lg text-sm whitespace-pre-wrap bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30">
                    {l.cancellationReason}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2"><UserIcon size={16} /> Employee</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold">
                {l.user?.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{l.user?.name}</div>
                <div className="text-xs text-slate-500 truncate">{l.user?.designation || l.user?.email}</div>
              </div>
            </div>
            <div className="mt-3 text-sm grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                <div className="text-[11px] text-slate-500">ID</div>
                <div className="font-semibold text-sm">{l.user?.employeeId || '—'}</div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                <div className="text-[11px] text-slate-500">Dept</div>
                <div className="font-semibold text-sm truncate">{l.user?.department || '—'}</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="font-semibold mb-3">Timeline</div>
            <ul className="text-sm space-y-3">
              <TimelineRow label="Applied" date={l.createdAt} done />
              {l.reviewedAt && (
                <TimelineRow
                  label={l.status === 'approved' ? 'Approved' : l.status === 'rejected' ? 'Rejected' : 'Reviewed'}
                  date={l.reviewedAt}
                  done
                  color={l.status === 'approved' ? 'emerald' : 'rose'}
                />
              )}
              {l.cancelledAt && (
                <TimelineRow label="Cancelled" date={l.cancelledAt} done color="rose" />
              )}
              {l.status === 'pending' && (
                <TimelineRow label="Awaiting admin review" pending />
              )}
            </ul>
          </Card>
        </div>
      </div>

      {showCancel && (
        <CancelLeaveModal
          leave={l}
          onClose={() => setShowCancel(false)}
          onConfirmed={() => {
            setShowCancel(false);
            qc.invalidateQueries({ queryKey: ['leave', id] });
            qc.invalidateQueries({ queryKey: ['leaves'] });
          }}
        />
      )}

      {showReject && (
        <RejectLeaveModal
          leave={l}
          onClose={() => setShowReject(false)}
          onConfirmed={(note) => {
            decide.mutate(
              { status: 'rejected', adminNote: note },
              { onSuccess: () => setShowReject(false) }
            );
          }}
          submitting={decide.isPending}
        />
      )}
    </div>
  );
}

function Field({ label, value, sub, highlight }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 ${highlight ? 'text-lg font-bold text-brand-600' : 'text-sm font-medium'}`}>
        {value || '—'}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function TimelineRow({ label, date, done, pending, color = 'brand' }) {
  const colorMap = {
    brand: 'bg-brand-500',
    emerald: 'bg-emerald-500',
    rose: 'bg-rose-500',
  };
  return (
    <li className="flex items-start gap-3">
      <div className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${pending ? 'bg-amber-400 animate-pulse' : colorMap[color] || 'bg-brand-500'}`} />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm">{label}</div>
        {date && <div className="text-xs text-slate-500">{fmtDateTime(date)}</div>}
        {pending && <div className="text-xs text-amber-600 dark:text-amber-400">In review</div>}
      </div>
    </li>
  );
}

function CancelLeaveModal({ leave, onClose, onConfirmed }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const wasApproved = leave.status === 'approved';

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error('Please write a reason');
      return;
    }
    setSubmitting(true);
    try {
      await leaveService.cancel(leave._id, { reason: trimmed });
      toast.success('Leave cancelled');
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Cancel leave"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={submitting}>Back</button>
          <button onClick={handleSubmit} className="btn-danger" disabled={submitting || !reason.trim()}>
            <X size={14} /> {submitting ? 'Cancelling...' : 'Cancel leave'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {wasApproved && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-700 dark:text-amber-300">
              This leave was already approved. Admins will be notified about the cancellation.
            </div>
          </div>
        )}
        <div>
          <label className="label">Reason for cancellation</label>
          <textarea
            rows={4}
            className="input"
            placeholder="Why are you cancelling this leave?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}

function RejectLeaveModal({ leave, onClose, onConfirmed, submitting }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      open
      onClose={onClose}
      title="Reject leave request"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={submitting}>Cancel</button>
          <button
            onClick={() => reason.trim() && onConfirmed(reason.trim())}
            className="btn-danger"
            disabled={!reason.trim() || submitting}
          >
            <X size={14} /> {submitting ? 'Rejecting...' : 'Reject leave'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30">
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-rose-700 dark:text-rose-300">
            You're rejecting <b>{leave.user?.name}</b>'s leave. The employee will be notified along with your note.
          </div>
        </div>
        <div>
          <label className="label">Reason for rejection</label>
          <textarea
            rows={4}
            className="input"
            placeholder="Let the employee know why you're rejecting this request..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}
